// Single comment thread for one company row. Posted comments list above an empty
// composer: Save appends a card (author + timestamp) instead of leaving text in the input.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commentsService, type CommentsListResponse } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTimeShort } from '@/lib/datetime';
import type { Comment } from '@/types';
import './comment-modal.css';

export interface CompanyCommentSlotsProps {
  resultId: string;
  companyName: string;
  /** Preview tab / shared-result viewing: comments are visible but not editable. */
  readOnly?: boolean;
  /** Compact layout for the result-detail side card. */
  compact?: boolean;
  /** Called after a successful save or delete. */
  onChanged?: () => void;
}

function authorLabel(
  user: {
    profile?: { firstName?: string; lastName?: string };
    username?: string;
  } | null,
): string {
  const first = user?.profile?.firstName?.trim() ?? '';
  const last = user?.profile?.lastName?.trim() ?? '';
  const full = `${first} ${last}`.trim();
  return full || user?.username || 'You';
}

function sortComments(comments: Comment[]): Comment[] {
  return [...comments].sort((a, b) => {
    const ta = Date.parse(a.createdAt) || 0;
    const tb = Date.parse(b.createdAt) || 0;
    return ta - tb;
  });
}

export default function CompanyCommentSlots({
  resultId,
  companyName,
  readOnly = false,
  compact = false,
  onChanged,
}: CompanyCommentSlotsProps) {
  const { currentUser, isAdmin, isOrgAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const cacheKey = ['comments', resultId, 'company', companyName] as const;

  const { data } = useQuery({
    queryKey: cacheKey,
    queryFn: () => commentsService.getForCompany(resultId, companyName),
    enabled: Boolean(resultId && companyName),
  });

  const comments = sortComments(data?.comments ?? []);

  const writeCache = (next: Comment[]) => {
    queryClient.setQueryData<CommentsListResponse>(cacheKey, {
      success: true,
      comments: next,
      count: next.length,
    });
  };

  const afterMutate = () => {
    void queryClient.invalidateQueries({ queryKey: ['comments', resultId] });
    onChanged?.();
  };

  const fallbackComment = (text: string, id?: string): Comment => ({
    id: id ?? `pending-${Date.now()}`,
    resultId,
    companyName,
    position: 1,
    text,
    createdBy: currentUser?.id ?? '',
    createdByName: authorLabel(currentUser),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const post = useMutation({
    mutationFn: (text: string) =>
      commentsService.save({ resultId, companyName, position: 1, text }),
    onSuccess: (res, text) => {
      const posted = res.comment ?? fallbackComment(text.trim());
      writeCache([...comments.filter((c) => c.id !== posted.id), posted]);
      setDraft('');
      afterMutate();
    },
  });

  const update = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      commentsService.save({ resultId, companyName, position: 1, text, commentId: id }),
    onSuccess: (res, vars) => {
      const posted = res.comment ?? fallbackComment(vars.text.trim(), vars.id);
      writeCache(comments.map((c) => (c.id === vars.id ? { ...c, ...posted, id: vars.id } : c)));
      setEditingId(null);
      setEditText('');
      afterMutate();
    },
  });

  const remove = useMutation({
    mutationFn: (commentId: string) => commentsService.remove(commentId, resultId),
    onSuccess: (_res, commentId) => {
      writeCache(comments.filter((c) => c.id !== commentId));
      if (editingId === commentId) {
        setEditingId(null);
        setEditText('');
      }
      afterMutate();
    },
  });

  const busy = post.isPending || update.isPending || remove.isPending;
  const failed = post.isError || update.isError || remove.isError;

  return (
    <div className={compact ? 'comment-thread comment-thread--compact' : 'comment-thread'}>
      {comments.length === 0 && readOnly ? (
        <div className="no-comments-message">No comments yet.</div>
      ) : null}

      {comments.length > 0 ? (
        <ul className="comment-thread-list">
          {comments.map((comment) => {
            const isAuthor = !!currentUser && String(comment.createdBy) === String(currentUser.id);
            const canDelete = isAuthor || isAdmin || isOrgAdmin;
            const editing = editingId === comment.id;
            return (
              <li key={comment.id} className="comment-item">
                <div className="comment-header">
                  <span className="comment-author">{comment.createdByName}</span>
                  <span className="comment-timestamp">
                    {formatDateTimeShort(comment.updatedAt || comment.createdAt)}
                    {comment.updatedAt !== comment.createdAt && ' (edited)'}
                  </span>
                </div>
                {editing ? (
                  <div className="comment-editor-container">
                    <textarea
                      className="comment-textarea"
                      rows={3}
                      value={editText}
                      disabled={busy}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                    <div className="comment-btn-group">
                      <button
                        type="button"
                        className="btn btn-standard btn-sm"
                        disabled={busy || !editText.trim()}
                        onClick={() => update.mutate({ id: comment.id, text: editText })}
                      >
                        Update
                      </button>
                      <button
                        type="button"
                        className="btn btn-standard btn-sm"
                        disabled={busy}
                        onClick={() => {
                          setEditingId(null);
                          setEditText('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="comment-text">{comment.text}</div>
                )}
                {!readOnly && (isAuthor || canDelete) && !editing && (
                  <div className="comment-actions">
                    {isAuthor && (
                      <button
                        type="button"
                        className="btn btn-standard btn-sm"
                        disabled={busy}
                        onClick={() => {
                          setEditingId(comment.id);
                          setEditText(comment.text);
                        }}
                      >
                        <i className="bi bi-pencil me-1" />
                        Edit
                      </button>
                    )}
                    {canDelete && !!comment.id && (
                      <button
                        type="button"
                        className="btn btn-standard btn-sm"
                        disabled={busy}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          remove.mutate(comment.id);
                        }}
                      >
                        <i className="bi bi-trash me-1" />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {!readOnly && (
        <div className="comment-editor-container">
          <textarea
            className="comment-textarea"
            rows={3}
            placeholder="Add a comment…"
            value={draft}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Add a comment"
          />
          <div className="comment-btn-group">
            <button
              type="button"
              className="btn btn-standard btn-sm"
              disabled={busy || !draft.trim()}
              onClick={() => post.mutate(draft)}
            >
              {post.isPending ? 'Saving…' : 'Save comment'}
            </button>
          </div>
        </div>
      )}

      {failed && (
        <div className="text-danger mt-2">
          {remove.isError
            ? 'Failed to delete — please try again.'
            : 'Failed to save — please try again.'}
        </div>
      )}
    </div>
  );
}
